import { useState, useEffect } from 'react';
import axios from '../../api/axios';
import './WhatsAppConfig.css';

const WhatsAppConfig = () => {
  const [config, setConfig] = useState({
    evolutionApiUrl: '',
    apiKey: '',
    isActive: true
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/superadmin/whatsapp-config');
      setConfig(response.data);
    } catch (error) {
      console.error('Error loading config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put('/superadmin/whatsapp-config', config);
      alert('WhatsApp configuration saved successfully');
      setTestResult(null); // Clear previous test results
    } catch (error) {
      alert('Error saving configuration: ' + (error.response?.data?.message || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const response = await axios.post('/superadmin/whatsapp-config/test-connection');
      setTestResult(response.data);
      
      if (response.data.success) {
        alert('✓ Connection successful! Evolution API is reachable and authentication is working.');
      } else {
        alert('✗ Connection failed: ' + response.data.message);
      }
    } catch (error) {
      setTestResult({
        success: false,
        error: error.message,
        message: 'Failed to test connection'
      });
      alert('Error testing connection: ' + (error.response?.data?.message || error.message));
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return <div className="wa-config-loading">Loading configuration...</div>;
  }

  return (
    <div className="wa-config-container">
      <div className="wa-config-header">
        <h1>WhatsApp Configuration</h1>
        <p>Configure Evolution API settings for all companies</p>
      </div>

      <div className="wa-config-card">
        <div className="wa-config-form">
          <div className="wa-config-form-group">
            <label>Evolution API URL *</label>
            <input
              type="text"
              value={config.evolutionApiUrl}
              onChange={(e) => setConfig({...config, evolutionApiUrl: e.target.value})}
              placeholder="http://localhost:8088"
            />
            <small>Base URL of your Evolution API instance</small>
          </div>

          <div className="wa-config-form-group">
            <label>API Key</label>
            <input
              type="password"
              value={config.apiKey || ''}
              onChange={(e) => setConfig({...config, apiKey: e.target.value})}
              placeholder="Optional API key"
            />
            <small>Leave empty if no API key is configured</small>
          </div>

          <div className="wa-config-form-group">
            <label className="wa-config-checkbox">
              <input
                type="checkbox"
                checked={config.isActive}
                onChange={(e) => setConfig({...config, isActive: e.target.checked})}
              />
              <span>Enable WhatsApp Integration</span>
            </label>
            <small>Disabling will prevent all companies from using WhatsApp features</small>
          </div>

          <div className="wa-config-actions">
            <button 
              onClick={handleSave} 
              disabled={saving || !config.evolutionApiUrl}
              className="wa-config-btn-save"
            >
              {saving ? 'Saving...' : 'Save Configuration'}
            </button>
            <button 
              onClick={handleTestConnection} 
              disabled={testing || !config.evolutionApiUrl}
              className="wa-config-btn-test"
            >
              {testing ? 'Testing...' : 'Test Connection'}
            </button>
          </div>

          {testResult && (
            <div className={`wa-config-test-result ${testResult.success ? 'success' : 'error'}`}>
              <h4>{testResult.success ? '✓ Connection Successful' : '✗ Connection Failed'}</h4>
              <p>{testResult.message}</p>
              {testResult.status && <p><strong>HTTP Status:</strong> {testResult.status}</p>}
              {testResult.url && <p><strong>URL:</strong> {testResult.url}</p>}
              {testResult.evolutionApiVersion && (
                <p><strong>Evolution API Version:</strong> {testResult.evolutionApiVersion}</p>
              )}
              {testResult.hasApiKey !== undefined && (
                <p><strong>API Key:</strong> {testResult.hasApiKey ? 'Configured' : 'Not configured'}</p>
              )}
              {testResult.error && (
                <details>
                  <summary>Error Details</summary>
                  <pre>{testResult.error}</pre>
                </details>
              )}
              {!testResult.success && testResult.status === 401 && (
                <div className="wa-config-help">
                  <strong>Authentication Error:</strong>
                  <ul>
                    <li>Check that Evolution API has <code>AUTHENTICATION_API_KEY</code> set in its .env file</li>
                    <li>The API key here must EXACTLY match Evolution API's global key</li>
                    <li>Restart Evolution API after changing the .env file</li>
                    <li>See <code>WHATSAPP_TROUBLESHOOTING.md</code> for detailed help</li>
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="wa-config-info">
          <h3>Setup Instructions</h3>
          <ol>
            <li>Install Evolution API using Docker or manually</li>
            <li>Enter the base URL of your Evolution API instance</li>
            <li>If you've configured an API key in Evolution API, enter it here</li>
            <li>Enable the integration to allow companies to connect WhatsApp</li>
          </ol>

          <h3>Evolution API Docker Command</h3>
          <pre className="wa-config-code">
docker run -d \
  --name evolution-api \
  -p 8088:8080 \
  atendai/evolution-api:latest
          </pre>

          <p className="wa-config-note">
            <strong>Note:</strong> Each company will get a unique instance name based on their company name and ID.
          </p>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppConfig;
